import { memo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormatter } from "@/utils/useFormatter";
import { useAffiliationOrganizationSearch } from "../hooks/useAffiliationOrganizationSearch";
import type {
  AffiliationActionResult,
  AffiliationOrganization,
  AffiliationOrganizationOption,
} from "../types";

interface AddAffiliationDialogProps {
  isOpen: boolean;
  isSaving: boolean;
  /**
   * Já listadas: aparecem desabilitadas na busca ("já está na lista").
   * A própria organização nem aparece (a busca já a remove).
   */
  blockedTokens: Set<string>;
  onClose: () => void;
  onConfirm: (organization: AffiliationOrganization) => Promise<AffiliationActionResult>;
  /** Textos opcionais (ex.: "Selecionar prestadora"); sem eles, os de adicionar afiliada. */
  title?: string;
  description?: string;
  confirmLabel?: string;
}

const DEFAULT_TITLE = "Adicionar organização atendida";
const DEFAULT_DESCRIPTION =
  "A organização escolhida passa a receber a teleconsultoria da sua organização. Cada organização pode ser atendida por apenas uma prestadora.";
const DEFAULT_CONFIRM_LABEL = "Adicionar";

/** Busca uma organização e confirma a afiliação em um único passo. */
export const AddAffiliationDialog = memo(function AddAffiliationDialog({
  isOpen,
  isSaving,
  blockedTokens,
  onClose,
  onConfirm,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  confirmLabel = DEFAULT_CONFIRM_LABEL,
}: AddAffiliationDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { options, isSearching, setSearchTerm } =
    useAffiliationOrganizationSearch(isOpen);
  const [selected, setSelected] = useState<AffiliationOrganizationOption | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const handleClose = () => {
    if (isSaving) return;
    setSelected(null);
    setErrorMessage(undefined);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setErrorMessage(undefined);
    const result = await onConfirm({ token: selected.token, name: selected.name });
    if (!result.isSuccess) {
      setErrorMessage(result.errorMessage);
      return;
    }
    setSelected(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          <Autocomplete
            options={options}
            value={selected}
            loading={isSearching}
            // A busca é feita no servidor: não refiltra no cliente.
            filterOptions={(currentOptions) => currentOptions}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.token === value.token}
            getOptionDisabled={(option) => blockedTokens.has(option.token)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") setSearchTerm(value);
            }}
            onChange={(_, value) => {
              setSelected(value);
              setErrorMessage(undefined);
            }}
            noOptionsText="Nenhuma organização encontrada"
            renderOption={(props, option) => (
              <li {...props} key={option.token}>
                <Stack>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    CNPJ {useFormatter.formatCNPJ(option.cnpj)}
                    {blockedTokens.has(option.token) ? " · já está na lista" : ""}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label="Buscar por nome ou CNPJ"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={!selected || isSaving}
          sx={{ textTransform: "none" }}
        >
          {isSaving ? "Salvando..." : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
